"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { useSession } from "next-auth/react";
import { storeSessionForRedirect, trackFunnelEvent } from "@/libs/ga";

interface UseGooglePopupAuthReturn {
  isAuthenticating: boolean;
  isSuccess: boolean;
  error: string | null;
  initiateGoogleAuth: (callbackUrl?: string) => Promise<void>;
  resetAuth: () => void;
}

/**
 * Handle Google OAuth in a popup window so the host page is never reloaded.
 *
 * Flow:
 * 1. Parent opens `/auth/popup/google?callbackUrl=...` in a popup window.
 * 2. That page triggers `signIn('google')` which redirects through Google.
 * 3. When Google bounces back, the popup page sees `status === 'authenticated'`
 *    and calls `window.close()`.
 * 4. The parent window regains focus; NextAuth's SessionProvider refetches
 *    the session automatically on window focus, so `useSession()` flips to
 *    `authenticated` without a page reload.
 * 5. The `status` transition below triggers `isSuccess` so the consumer can
 *    close the auth modal.
 *
 * We only poll `popup.closed` to detect user-cancelled sign-ins (popup closed
 * without ever reaching `authenticated`).
 */
/**
 * Translate a NextAuth error code into a user-facing message. NextAuth returns
 * short, opaque codes like "Callback" or "OAuthSignin"; most of the time the
 * real cause (Mongo down, misconfigured client, etc.) lives in the server log,
 * so the UI copy just needs to invite a retry without being alarming.
 */
function friendlyAuthError(code: string | null | undefined): string {
  if (!code) return "Sign-in failed. Please try again.";
  switch (code) {
    case "OAuthAccountNotLinked":
      return "This email is already linked to a different sign-in method.";
    case "AccessDenied":
      return "Access was denied. Please try again.";
    case "Configuration":
      return "Sign-in is temporarily unavailable. Please try again in a moment.";
    default:
      return "Sign-in failed. Please try again.";
  }
}

export function useGooglePopupAuth(): UseGooglePopupAuthReturn {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cancelCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Interval closures would otherwise read stale status.
  const statusRef = useRef<"loading" | "authenticated" | "unauthenticated">("unauthenticated");
  const isSuccessRef = useRef(false);
  const isErrorRef = useRef(false);
  const { data: session, status, update: updateSession } = useSession();
  const previousStatusRef = useRef(status);

  statusRef.current = status;

  const cleanup = useCallback((opts?: { preservePostCloseCheck?: boolean }) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (!opts?.preservePostCloseCheck && cancelCheckTimeoutRef.current) {
      clearTimeout(cancelCheckTimeoutRef.current);
      cancelCheckTimeoutRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
    }
  }, []);

  const markSuccess = useCallback(() => {
    if (isSuccessRef.current) return;
    isSuccessRef.current = true;
    setIsSuccess(true);
    setIsAuthenticating(false);
    cleanup();
    trackFunnelEvent("acquisition", "signin_completed", {
      auth_method: "google",
      auth_flow: "popup",
    });
  }, [cleanup]);

  // Detect session transition unauthenticated -> authenticated while the popup
  // is active; that's the secondary signal that OAuth succeeded (the primary
  // one is the explicit postMessage from the popup page).
  useEffect(() => {
    if (
      isAuthenticating &&
      previousStatusRef.current !== "authenticated" &&
      status === "authenticated" &&
      session?.user
    ) {
      markSuccess();
    }
    previousStatusRef.current = status;
  }, [status, session, isAuthenticating, markSuccess]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Listen for explicit messages from the popup. This is the authoritative
  // signal for both success and failure; the session-transition effect above
  // and the `popup.closed` poll below are only fallbacks.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data || data.type !== "googlePopupAuth") return;

      if (data.status === "error") {
        if (isSuccessRef.current) return;
        isErrorRef.current = true;
        setError(friendlyAuthError(data.error));
        setIsAuthenticating(false);
        cleanup();

        trackFunnelEvent("acquisition", "signin_error", {
          auth_method: "google",
          auth_flow: "popup",
          error: data.error || "unknown",
        });
        return;
      }

      if (data.status === "success") {
        // Primary success path: the popup told us auth is done. Flip
        // success state IMMEDIATELY so the modal can close and so the
        // popup-closed poll can't race in behind us and overwrite us
        // with "Sign-in cancelled" (the cancel timer waits 1s before
        // firing and /api/auth/session can easily take longer than that).
        markSuccess();
        // Warm the parent's session cache so downstream consumers of
        // useSession() (sidebar, credits context, …) see the authenticated
        // user without waiting for the window-focus refetch. Fire-and-forget.
        updateSession().catch(() => {});
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [cleanup, markSuccess, updateSession]);

  const initiateGoogleAuth = useCallback(
    async (callbackUrl: string = "/") => {
      try {
        setIsAuthenticating(true);
        setError(null);
        setIsSuccess(false);
        isSuccessRef.current = false;
        isErrorRef.current = false;

        storeSessionForRedirect("oauth");

        trackFunnelEvent("acquisition", "signin_started", {
          auth_method: "google",
          auth_flow: "popup",
          callback_url: callbackUrl,
        });

        const width = 500;
        const height = 600;
        // Center the popup relative to the browser window viewport
        const left = window.screenLeft + (window.outerWidth - width) / 2;
        const top = window.screenTop + (window.outerHeight - height) / 2;

        const authUrl = `/auth/popup/google?${new URLSearchParams({ callbackUrl })}`;

        const popup = window.open(
          authUrl,
          "Google Sign In",
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,menubar=no,scrollbars=yes,status=yes`
        );

        if (!popup) {
          throw new Error(
            "Google sign-in popup blocked. Please allow popups for this site in your browser settings."
          );
        }

        popupRef.current = popup;

        // Polling `popup.closed` on a cross-origin (Google) window triggers
        // Cross-Origin-Opener-Policy console warnings in Chrome. The primary
        // success/error signal now comes from postMessage inside the popup -
        // this poll is only a fallback for the case where the user manually
        // closes the popup window before auth finishes. Delaying the start
        // cuts down the warning noise without affecting correctness.
        const pollDelayMs = 1500;
        const pollIntervalMs = 1000;
        const startPolling = () => {
          pollIntervalRef.current = setInterval(() => {
          try {
            if (isSuccessRef.current || isErrorRef.current) {
              cleanup();
              return;
            }
            if (popup.closed) {
              cancelCheckTimeoutRef.current = setTimeout(() => {
                cancelCheckTimeoutRef.current = null;
                if (isSuccessRef.current) return;
                if (isErrorRef.current) return;
                if (statusRef.current === "authenticated") return;
                setIsAuthenticating(false);
                setError("Sign-in cancelled");
                cleanup();

                trackFunnelEvent("acquisition", "signin_cancelled", {
                  auth_method: "google",
                  auth_flow: "popup",
                });
              }, 1000);
              cleanup({ preservePostCloseCheck: true });
            }
          } catch {
            // Silently ignore cross-origin access errors.
          }
          }, pollIntervalMs);
        };

        const startTimer = setTimeout(() => {
          // Skip starting the poll entirely if we've already resolved.
          if (isSuccessRef.current || isErrorRef.current) return;
          startPolling();
        }, pollDelayMs);
        cancelCheckTimeoutRef.current = startTimer;
      } catch (err: any) {
        console.error("[useGooglePopupAuth] Error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
        setIsAuthenticating(false);
        cleanup();

        trackFunnelEvent("acquisition", "signin_error", {
          auth_method: "google",
          auth_flow: "popup",
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    [cleanup]
  );

  const resetAuth = useCallback(() => {
    setIsAuthenticating(false);
    setIsSuccess(false);
    isSuccessRef.current = false;
    isErrorRef.current = false;
    setError(null);
    cleanup();
  }, [cleanup]);

  return {
    isAuthenticating,
    isSuccess,
    error,
    initiateGoogleAuth,
    resetAuth,
  };
}
